import { useCallback, useEffect, useMemo, useState } from "react";

import { Box, Flex, Image, useDisclosure } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { useShallow } from "zustand/react/shallow";

import { DeliveryRolliV2 } from "__graphql__/types";
import { EppoFeatureFlags } from "app/types/flags";
import { routes } from "config/routes";
import { ConnectionErrorPlaceholder } from "flows/Activities/components/ConnectionErrorPlaceholder";
import { ExitFlowInRolliValidationModal } from "flows/Activities/components/ExitFlowInRolliValidationModal";
import {
  RolliIdBarcodeError,
  ManualRolliIdInput,
} from "flows/Activities/components/ManualRolliIdInput/ManualRolliIdInput";
import { RolliListContainer } from "flows/Activities/components/RolliListContainer";
import { TimePickerModal } from "flows/Activities/components/TimePickerModal";
import {
  DeliveryDataUnavailableModal,
  PotentialIncorrectRolliModal,
  RolliContainsCratesModal,
  WrongRolliModal,
} from "flows/Activities/components/WarningModals";
import { useCreateDeliveryCheckinForHubV2Mutation } from "flows/Activities/queries/deliveryCheckin/deliveryCheckin.generated";
import { useGetSuppliersForHubQuery } from "flows/Activities/queries/supplier/supplier.generated";
import { ModalNames, useCheckinUIStore } from "flows/Activities/stores/useCheckInUIStore";
import { useDeliveryStore } from "flows/Activities/stores/useDeliveryStore";
import {
  CREATE_DELIVERY_CHECK_IN_ERROR_TOAST_ID,
  LEKKERLAND_PROD_ID,
  LEKKERLAND_STAGING_ID,
  OTHER_SUPPLIER_NAME,
  REWE_PROD_ID,
  REWE_STAGING_ID,
  SUPPLIERS_WITH_ROLLIES,
} from "flows/Activities/utils/constants";
import DeliveryTruck from "images/random/delivery-truck.svg";
import ReweLogo from "images/random/rewe-logo.png";
import { Page } from "shared/components/Page";
import { useCustomToast } from "shared/hooks/useCustomToast";
import { useEppoFeatureFlagProvider } from "shared/hooks/useEppoFeatureFlag";
import { BOTTOM_TAB_BAR_HEIGHT_NUMBER } from "ui/BottomTabBarContainer";
import { Button } from "ui/Button/Button";
import { DropDown } from "ui/DropDown/DropDown";
import { DropDownOption } from "ui/DropDown/DropDownList";
import { PenIcon } from "ui/Icons/Icons";
import { NavigationHeader } from "ui/NavigationHeader/NavigationHeader";
import { TitleM } from "ui/Typography/Typography";
import { isNotNullNorUndefined, isNullOrUndefined } from "utils/tsHelpers";

import { serializeSupplier } from "../../models/supplier/serializer";

function CheckIn({
  supplierOptions,
  selectedTime,
  onOpenAdjustTimeModal,
  selectedTimeAsISOString,
}: {
  supplierOptions: (DropDownOption & { externalId: string })[];
  selectedTime: string;
  onOpenAdjustTimeModal: () => void;
  selectedTimeAsISOString: string;
}) {
  const navigate = useNavigate();
  const { showToastUI } = useCustomToast();

  const [selectedSupplierId, setSelectedSupplierId] = useState<string | null>(null);
  const { supplierBgColor, supplierTextColor } = useMemo(() => {
    return {
      supplierBgColor: selectedSupplierId ? "pinkFlink.100" : "grey.100",
      supplierTextColor: selectedSupplierId ? "pinkFlink.500" : "",
    };
  }, [selectedSupplierId]);

  const { isFeatureEnabled: isTransportEquipmentFlowEnabled } = useEppoFeatureFlagProvider(
    EppoFeatureFlags.ENABLE_TRANSPORT_EQUIPMENT_FLOW,
  );

  const { setDeliveryInfo } = useDeliveryStore(
    useShallow((state) => ({
      setDeliveryInfo: state.setDeliveryInfo,
    })),
  );

  const {
    scannedRollies,
    shouldShowManualRolliIdInput,
    hasRolliIdBarcodeError,
    modals,
    setShowModal,
    reset: resetCheckinUIStore,
  } = useCheckinUIStore(
    useShallow((state) => ({
      scannedRollies: state.scannedRollies,
      shouldShowManualRolliIdInput: state.shouldShowManualRolliIdInput,
      hasRolliIdBarcodeError: state.hasRolliIdBarcodeError,
      modals: state.modals,
      reset: state.reset,
      setShowModal: state.setShowModal,

      setCheckinUIState: state.setCheckinUIState,
    })),
  );
  const hasValidSupplier = isNotNullNorUndefined(selectedSupplierId);
  const hasRollies = selectedSupplierId && SUPPLIERS_WITH_ROLLIES.includes(selectedSupplierId);
  const hasScannedRollies = !!(scannedRollies && scannedRollies.length > 0);
  const shouldShowActionComponent = hasRollies
    ? !shouldShowManualRolliIdInput && (hasScannedRollies || hasRolliIdBarcodeError)
    : hasValidSupplier;
  const shouldShowComfirmCTA = hasRollies ? hasScannedRollies : true;
  const [createDeliveryCheckinForHub, { loading: isUpdating }] =
    useCreateDeliveryCheckinForHubV2Mutation({
      onCompleted: () => {
        if (
          isTransportEquipmentFlowEnabled &&
          selectedSupplierId &&
          [REWE_PROD_ID, REWE_STAGING_ID, LEKKERLAND_PROD_ID, LEKKERLAND_STAGING_ID].includes(
            selectedSupplierId,
          )
        ) {
          navigate(routes.activities.deliveryCheckIn.transportEquipmentConfirm);
        } else {
          navigate("/");
        }
        resetCheckinUIStore();
      },
      onError: () => {
        showToastUI({
          id: CREATE_DELIVERY_CHECK_IN_ERROR_TOAST_ID,
          title: "pages.delivery-check-in.error.title",
          description: "pages.delivery-check-in.error.description",
        });
      },
    });

  const canConfirmDeliveryCheckInForHub = useMemo(() => {
    if (hasRollies) {
      if (!scannedRollies || scannedRollies.length === 0) {
        return false;
      }
      return scannedRollies.every((scannedRolli) => scannedRolli.status !== null);
    }
    return hasValidSupplier;
  }, [hasRollies, hasValidSupplier, scannedRollies]);

  const confirmDeliveryCheckinForHub = useCallback(async () => {
    if (isNullOrUndefined(selectedSupplierId) || !canConfirmDeliveryCheckInForHub) {
      return;
    }
    createDeliveryCheckinForHub({
      variables: {
        input: {
          supplierId: selectedSupplierId,
          deliveredAt: selectedTimeAsISOString,
          deliveryRollies: scannedRollies?.map(
            (scannedRolli) =>
              ({
                desadvId: scannedRolli.desadv_id,
                expectedHubSlug: scannedRolli.expected_hub,
                sscc: scannedRolli.sscc,
                status: scannedRolli.status,
              }) as DeliveryRolliV2,
          ),
        },
      },
    });
  }, [
    canConfirmDeliveryCheckInForHub,
    createDeliveryCheckinForHub,
    scannedRollies,
    selectedSupplierId,
    selectedTimeAsISOString,
  ]);

  useEffect(() => {
    if (selectedSupplierId !== null && selectedTimeAsISOString !== null) {
      const externalSupplierId =
        supplierOptions.find((option) => option.key === selectedSupplierId)?.externalId ?? null;
      setDeliveryInfo({
        supplierId: selectedSupplierId,
        externalSupplierId,
        deliveryTime: selectedTimeAsISOString,
        deliveryRollies: scannedRollies ?? [],
      });
    }
  }, [
    supplierOptions,
    selectedSupplierId,
    selectedTimeAsISOString,
    scannedRollies,
    setDeliveryInfo,
  ]);

  const isREWE = selectedSupplierId && [REWE_STAGING_ID, REWE_PROD_ID].includes(selectedSupplierId);
  const logoImg = isREWE ? ReweLogo : DeliveryTruck;

  return (
    <>
      <Flex
        direction="column"
        backgroundColor="grey.100"
        w="100%"
        h="100%"
        py="s200"
        px="s100"
        overflow="hidden"
      >
        <Flex
          align="center"
          p="s200"
          gap="s300"
          bg="white"
          borderRadius={hasRollies ? "8px 8px 0 0" : "8px"}
          overflow="hidden"
        >
          <Image src={logoImg} borderRadius="sm" w="80px" />
          <Flex direction="column" gap="s100" flexGrow="1" overflow="hidden">
            <DropDown
              options={supplierOptions}
              value={selectedSupplierId}
              onInput={setSelectedSupplierId}
              filterMode={false}
              backgroundColor={supplierBgColor}
              titleColor={supplierTextColor}
              color={supplierTextColor}
              isDisabled={hasScannedRollies}
              _hover={hasScannedRollies ? { bg: "pinkFlink.100" } : {}}
              h="40px"
            >
              <FormattedMessage id="pages.delivery-check-in.supplier-dropdown" />
            </DropDown>
            <Flex direction="row" gap="s100">
              <Box bg="grey.100" borderRadius="sm">
                <TitleM alignSelf="center" color="grey.800" px="s200" py="s100">
                  {selectedTime}
                </TitleM>
              </Box>
              <Box
                bg="grey.100"
                onClick={onOpenAdjustTimeModal}
                cursor="pointer"
                borderRadius="sm"
                p="s100"
              >
                <PenIcon boxSize="24px" />
              </Box>
            </Flex>
          </Flex>
        </Flex>
        {hasRollies && (
          <>
            <RolliListContainer />
            <WrongRolliModal
              onClose={() => setShowModal(ModalNames.WRONG_ROLLI, false)}
              isOpen={modals.wrongRolliModal}
            />
            <RolliContainsCratesModal
              onClose={() => setShowModal(ModalNames.ROLLI_CONTAINS_CRATES, false)}
              isOpen={modals.rolliContainsCratesModal}
            />
            <PotentialIncorrectRolliModal
              onClose={() => setShowModal(ModalNames.POTENTIAL_INCORRECT_ROLLI, false)}
              isOpen={modals.potentialIncorrectRolliModal}
            />
            <DeliveryDataUnavailableModal
              onClose={() => setShowModal(ModalNames.DELIVERY_DATA_UNAVAILABLE, false)}
              isOpen={modals.deliveryDataUnavailableModal}
            />
            <ExitFlowInRolliValidationModal
              onClickConfirm={() => {
                setShowModal(ModalNames.EXIT_FLOW_IN_ROLLI_VALIDATION, false);
                resetCheckinUIStore();
                navigate(-1);
              }}
              onClickDecline={() => setShowModal(ModalNames.EXIT_FLOW_IN_ROLLI_VALIDATION, false)}
              isOpen={modals.exitFlowInRolliValidationModal}
            />
          </>
        )}
      </Flex>
      <ManualRolliIdInput />
      {shouldShowActionComponent && (
        <Flex
          flexDirection="column"
          justifyContent="center"
          w="100%"
          p="s200"
          bgColor="white"
          borderTopWidth="0.5px"
          borderTopColor="grey.150"
          align="center"
          position="absolute"
          bottom="0"
          minH={`${BOTTOM_TAB_BAR_HEIGHT_NUMBER}px`}
          gap="s100"
        >
          {hasRollies && <RolliIdBarcodeError />}
          {shouldShowComfirmCTA && (
            <Button
              isFullWidth
              onClick={confirmDeliveryCheckinForHub}
              disabled={!canConfirmDeliveryCheckInForHub}
              isLoading={isUpdating}
              data-testid="confirm_delivery_checkin"
            >
              <FormattedMessage id="pages.delivery-check-in.cta-button" />
            </Button>
          )}
        </Flex>
      )}
    </>
  );
}

export function DeliveryCheckIn() {
  const navigate = useNavigate();
  const intl = useIntl();

  const {
    setShowModal,
    scannedRollies,
    reset: resetCheckinUIStore,
  } = useCheckinUIStore(
    useShallow((state) => ({
      setShowModal: state.setShowModal,
      scannedRollies: state.scannedRollies,
      reset: state.reset,
    })),
  );

  // suppliers
  const {
    data: suppliersData,
    loading: isLoading,
    refetch,
    error,
  } = useGetSuppliersForHubQuery({
    fetchPolicy: "network-only",
  });

  const sortedSuppliers = useMemo(() => {
    if (isNullOrUndefined(suppliersData)) {
      return [];
    }
    const suppliers = suppliersData.getSuppliersForHub.suppliers.map(serializeSupplier);
    const translatedSuppliers = suppliers.map((supplier) => ({
      ...supplier,
      name:
        supplier.name === OTHER_SUPPLIER_NAME
          ? intl.formatMessage({
              id: "pages.activities.delivery-check-in-history.other-supplier",
              defaultMessage: OTHER_SUPPLIER_NAME,
            })
          : supplier.name,
    }));
    return translatedSuppliers.sort(
      (a, b) => (b.sortSequence || 0) - (a.sortSequence || 0) || a.name.localeCompare(b.name),
    );
  }, [suppliersData, intl]);

  const supplierOptions = useMemo(() => {
    return (
      sortedSuppliers.map(({ id, externalId, name }) => ({
        key: id,
        label: name,
        externalId,
      })) ?? []
    );
  }, [sortedSuppliers]);

  // selectedTime
  const [selectedTime, setSelectedTime] = useState(
    new Date().toLocaleTimeString("de-DE").slice(0, 5),
  );

  const {
    onOpen: onOpenAdjustTimeModal,
    isOpen: isAdjustTimeModalOpen,
    onClose: onCloseAdjustTimeModal,
  } = useDisclosure();

  const onAdjustTimeModalConfirm = useCallback(
    (value: string) => {
      setSelectedTime(value);
      onCloseAdjustTimeModal();
    },
    [setSelectedTime, onCloseAdjustTimeModal],
  );

  const selectedTimeAsISOString = useMemo(() => {
    const d = new Date();
    const [hours, minutes] = selectedTime.split(":").map((n) => parseInt(n, 10));
    d.setHours(hours, minutes, 0, 0);
    return d.toISOString();
  }, [selectedTime]);

  const handleGoBack = useCallback(() => {
    if (scannedRollies?.length) {
      setShowModal(ModalNames.EXIT_FLOW_IN_ROLLI_VALIDATION, true);
    } else {
      navigate(routes.activities.root);
    }
  }, [scannedRollies, setShowModal, navigate]);

  useEffect(() => {
    resetCheckinUIStore();
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isNotNullNorUndefined(error)) {
    return (
      <Page isCentered>
        <ConnectionErrorPlaceholder onClickCta={refetch} />
      </Page>
    );
  }

  return (
    <Page
      isCentered
      isFull
      justifyContent={isLoading ? "center" : "flex-start"}
      bgColor="grey.100"
      isLoading={isLoading}
    >
      <NavigationHeader
        title={intl.formatMessage({ id: "pages.delivery-check-in.title" })}
        onClickGoBack={handleGoBack}
      />
      <CheckIn
        supplierOptions={supplierOptions}
        selectedTime={selectedTime}
        onOpenAdjustTimeModal={onOpenAdjustTimeModal}
        selectedTimeAsISOString={selectedTimeAsISOString}
      />
      <TimePickerModal
        confirmLabel={intl.formatMessage({
          id: "pages.delivery-check-in.time-picker-modal.confirm",
          defaultMessage: "Set a time",
        })}
        cancelLabel={intl.formatMessage({
          id: "pages.delivery-check-in.time-picker-modal.cancel",
          defaultMessage: "Cancel",
        })}
        isOpen={isAdjustTimeModalOpen}
        onClickCancel={onCloseAdjustTimeModal}
        onClickConfirm={onAdjustTimeModalConfirm}
        initialValue={selectedTime}
      />
    </Page>
  );
}
